import { useState } from "react";
import { CreditsBadge } from "./CreditsBadge";
import type { CreditsInfo } from "../credits";

interface HeroSectionProps {
  onAnalyze: (url: string, jobDescriptionFallback?: string) => void;
  error?: string;
  creditsInfo: CreditsInfo | null;
  userEmail: string;
  onSignOut: () => void;
}

const PLATFORM_PATTERNS: Record<string, { label: string; dot: string }> = {
  "linkedin.com": { label: "LinkedIn", dot: "#0077b5" },
  "indeed.com": { label: "Indeed", dot: "#2164f3" },
  "naukri.com": { label: "Naukri", dot: "#ff7555" },
  "wellfound.com": { label: "Wellfound", dot: "#3cc68a" },
  "angel.co": { label: "Wellfound", dot: "#3cc68a" },
};

function detectPlatform(url: string): { label: string; dot: string } | null {
  try {
    const hostname = new URL(url).hostname;
    for (const [pattern, info] of Object.entries(PLATFORM_PATTERNS)) {
      if (hostname.includes(pattern)) return info;
    }
  } catch {}
  return null;
}

export function HeroSection({ onAnalyze, error, creditsInfo, userEmail, onSignOut }: HeroSectionProps) {
  const [url, setUrl] = useState("");
  const [showFallback, setShowFallback] = useState(false);
  const [fallbackText, setFallbackText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const detectedPlatform = detectPlatform(url);
  const isIndeed = detectedPlatform?.label === "Indeed";
  const canSubmit = url.trim() || fallbackText.trim();
  const shouldShowFallback = showFallback || isIndeed || (error?.includes("scrape") ?? false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onAnalyze(url.trim() || "https://example.com/job", fallbackText.trim() || undefined);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen min-h-dvh flex flex-col items-center justify-center px-4 sm:px-6 py-12 sm:py-16 relative overflow-hidden">
      {/* Background radial mesh */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse 60% 50% at 50% 40%, rgba(255,68,68,0.04) 0%, transparent 70%),
            radial-gradient(ellipse 40% 40% at 30% 60%, rgba(129,140,248,0.03) 0%, transparent 70%)
          `,
        }}
      />

      {/* Horizontal scan line */}
      <div
        aria-hidden
        className="absolute left-0 right-0 h-px pointer-events-none hidden sm:block"
        style={{
          top: "30%",
          background: "linear-gradient(90deg, transparent, rgba(255,68,68,0.08), transparent)",
        }}
      />

      <div className="relative z-10 w-full max-w-lg">
        {/* Status badge + user info */}
        <div className="flex flex-col items-center mb-8 sm:mb-12">
          <span
            className="inline-flex items-center gap-2 sm:gap-2.5 px-3 sm:px-4 py-1.5 rounded-full"
            style={{
              border: "1px solid rgba(255,68,68,0.15)",
              background: "rgba(255,68,68,0.05)",
            }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full animate-pulse-glow"
              style={{ backgroundColor: "#ff4444", boxShadow: "0 0 8px #ff444488" }}
            />
            <span className="mono-label" style={{ color: "rgba(255,68,68,0.6)" }}>
              Ghost Job Detector
            </span>
          </span>
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mt-2 max-w-full px-2">
            {creditsInfo && <CreditsBadge creditsInfo={creditsInfo} />}
            <span className="mono-label truncate max-w-[200px] sm:max-w-none" style={{ color: "var(--text-tertiary)" }}>
              {userEmail}
            </span>
            <button
              onClick={onSignOut}
              className="mono-label shrink-0 underline transition-colors duration-200"
              style={{ color: "var(--text-tertiary)" }}
              onMouseEnter={(e) => ((e.target as HTMLElement).style.color = "var(--ghost-red)")}
              onMouseLeave={(e) => ((e.target as HTMLElement).style.color = "var(--text-tertiary)")}
            >
              Sign out
            </button>
          </div>
        </div>

        {/* Headline — Instrument Serif */}
        <div className="text-center mb-2 sm:mb-3">
          <h1
            style={{ fontFamily: "var(--font-display)", lineHeight: 0.95 }}
            className="text-[clamp(2.75rem,10vw,6.5rem)] font-normal tracking-tight text-white"
          >
            Is this job
          </h1>
          <h1
            style={{
              fontFamily: "var(--font-display)",
              fontStyle: "italic",
              lineHeight: 0.95,
              color: "rgba(255,255,255,0.12)",
            }}
            className="text-[clamp(2.75rem,10vw,6.5rem)] font-normal tracking-tight"
          >
            even real?
          </h1>
        </div>

        {/* Ghost */}
        <div className="text-center text-4xl sm:text-5xl mb-5 sm:mb-6 animate-float select-none" aria-hidden>
          👻
        </div>

        {/* Subtext */}
        <p
          className="text-center text-xs sm:text-sm leading-relaxed mb-8 sm:mb-10 max-w-xs sm:max-w-sm mx-auto px-2"
          style={{ color: "var(--text-secondary)" }}
        >
          Ghost jobs are postings companies never intend to fill.
          <br className="hidden sm:block" />
          <span className="sm:hidden"> </span>
          We scrape the listing <em>and</em> investigate the company.
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-3">
          {/* URL input */}
          <div
            className="relative rounded-xl sm:rounded-2xl transition-all duration-300"
            style={{
              boxShadow: isFocused
                ? "0 0 0 1px rgba(255,255,255,0.15), 0 4px 24px rgba(0,0,0,0.4)"
                : "0 0 0 1px var(--border)",
              background: "var(--card)",
            }}
          >
            <input
              type="url"
              inputMode="url"
              autoCapitalize="none"
              autoCorrect="off"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="Paste a job listing URL…"
              className="w-full px-4 sm:px-5 py-3.5 sm:py-4 rounded-xl sm:rounded-2xl text-sm text-white placeholder-white/15 focus:outline-none bg-transparent"
              style={{ paddingRight: detectedPlatform ? "6.5rem" : "1rem" }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
            />
            {detectedPlatform && (
              <div
                className="absolute right-3 sm:right-3.5 top-1/2 -translate-y-1/2 flex items-center gap-1.5 px-2 sm:px-2.5 py-1 rounded-full"
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: "0.6rem",
                  fontWeight: 500,
                  background: `${detectedPlatform.dot}15`,
                  border: `1px solid ${detectedPlatform.dot}30`,
                  color: detectedPlatform.dot,
                }}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: detectedPlatform.dot }}
                />
                {detectedPlatform.label}
              </div>
            )}
          </div>

          {/* Indeed warning */}
          {isIndeed && (
            <div
              className="px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl text-xs animate-fade-in flex items-start gap-2"
              style={{
                background: "rgba(245,158,11,0.06)",
                border: "1px solid rgba(245,158,11,0.12)",
                color: "rgba(245,158,11,0.65)",
              }}
            >
              <span className="mt-px shrink-0">⚠</span>
              <span>Indeed blocks automated scraping. Paste the job description below for best results.</span>
            </div>
          )}

          {/* Fallback toggle */}
          <button
            type="button"
            onClick={() => setShowFallback(!showFallback)}
            className="w-full text-center transition-colors py-1.5 mono-label"
            style={{ color: "var(--text-tertiary)" }}
            onMouseEnter={(e) => ((e.target as HTMLElement).style.color = "var(--text-secondary)")}
            onMouseLeave={(e) => ((e.target as HTMLElement).style.color = "var(--text-tertiary)")}
          >
            {shouldShowFallback ? "▲ Hide description" : "▼ Paste job description"}
          </button>

          {shouldShowFallback && (
            <textarea
              value={fallbackText}
              onChange={(e) => setFallbackText(e.target.value)}
              placeholder="Paste the full job description here…"
              rows={5}
              className="w-full px-4 sm:px-5 py-3.5 sm:py-4 rounded-xl sm:rounded-2xl text-sm text-white placeholder-white/15 focus:outline-none resize-none animate-fade-in"
              style={{
                background: "var(--card)",
                border: "1px solid var(--border)",
              }}
            />
          )}

          {error && (
            <div
              className="px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl text-xs sm:text-sm animate-fade-in"
              style={{
                background: "rgba(255,68,68,0.06)",
                border: "1px solid rgba(255,68,68,0.15)",
                color: "#ff6666",
                fontFamily: "var(--font-mono)",
                fontSize: "0.75rem",
              }}
            >
              {error}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={!canSubmit || isSubmitting}
            className="w-full py-3.5 sm:py-4 rounded-xl sm:rounded-2xl font-semibold text-sm tracking-wide transition-all duration-300 active:scale-[0.98]"
            style={{
              background: canSubmit && !isSubmitting ? "#fff" : "rgba(255,255,255,0.08)",
              color: canSubmit && !isSubmitting ? "#08080a" : "rgba(255,255,255,0.2)",
              cursor: canSubmit && !isSubmitting ? "pointer" : "not-allowed",
              boxShadow: canSubmit && !isSubmitting ? "0 2px 16px rgba(255,255,255,0.1)" : "none",
            }}
          >
            {isSubmitting ? "Investigating…" : "Investigate This Job →"}
          </button>
        </form>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-3 mt-8 sm:mt-10">
          <span className="mono-label">Powered by Groq AI</span>
          <span className="hidden sm:inline" style={{ color: "var(--text-ghost)" }}>·</span>
          <span className="mono-label">Background checks via Jina</span>
        </div>
      </div>
    </div>
  );
}
